import React, { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  Buildings, Palette, UserPlus, CheckCircle, ArrowRight, ArrowLeft,
  UploadSimple, Image as ImageIcon, Database, FileArrowUp,
} from "@phosphor-icons/react";

const PRESET_COLORS = [
  "#002FA7", "#F97316", "#0EA5E9", "#10B981", "#8B5CF6",
  "#EC4899", "#F59E0B", "#EF4444", "#14B8A6", "#111827",
];

const STEPS = [
  { key: "identity", label: "Identity", icon: Buildings },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "team", label: "First staff", icon: UserPlus },
  { key: "seed", label: "Import data", icon: Database },
  { key: "done", label: "Ready", icon: CheckCircle },
];

/**
 * Guides a brand-new client organisation's admin through 4 steps:
 * 1. Confirm/edit the organisation name
 * 2. Upload logo + pick brand colours
 * 3. Create the first staff account
 * 4. Land in the dashboard
 *
 * Only fires when: user is admin/director of a NON-provider org whose
 * `onboarding_complete` flag is false. Providers and already-onboarded
 * tenants are skipped entirely.
 */
export default function OnboardingWizard({ children }) {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(user?.org?.name || "");
  const [logoUrl, setLogoUrl] = useState(user?.org?.logo_url || "");
  const [brandPrimary, setBrandPrimary] = useState(user?.org?.brand_primary || "#002FA7");
  const [brandAccent, setBrandAccent] = useState(user?.org?.brand_accent || "#FF6A00");
  const [contactEmail, setContactEmail] = useState(user?.org?.contact_email || "");
  const [contactPhone, setContactPhone] = useState(user?.org?.contact_phone || "");
  const [staff, setStaff] = useState({
    username: "", name: "", email: "", password: "", role: "staff",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const skip = !user
    || user.is_provider
    || user.must_change_password    // let the change-password gate run first
    || !user.org
    || user.org.onboarding_complete === true
    || !(user.role === "admin" || user.role === "director");

  if (skip) return children;

  async function saveIdentity() {
    if (!name.trim()) { toast.error("Organisation name is required"); return false; }
    try {
      await api.put(`/organizations/${user.org.id}`, {
        code: user.org.code,
        name: name.trim(),
        logo_url: logoUrl || null,
        brand_primary: brandPrimary,
        brand_accent: brandAccent,
        contact_email: contactEmail || null,
        contact_phone: contactPhone || null,
      });
      return true;
    } catch (e) { toast.error(formatError(e)); return false; }
  }

  async function handleLogoFile(f) {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.url);
      toast.success(data.background_removed ? "Logo uploaded — background removed" : "Logo uploaded");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  }

  async function saveStaff() {
    if (!staff.username || !staff.name || !staff.password) {
      toast.error("Username, name and password are required");
      return false;
    }
    if (staff.password.length < 6) { toast.error("Password must be at least 6 characters"); return false; }
    try {
      const payload = { ...staff };
      if (!payload.email) delete payload.email;
      await api.post("/users", payload);
      return true;
    } catch (e) { toast.error(formatError(e)); return false; }
  }

  async function finishOnboarding() {
    try {
      await api.post(`/organizations/${user.org.id}/complete-onboarding`);
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Setup complete — welcome to your control room!");
    } catch (e) { toast.error(formatError(e)); }
  }

  async function nextStep() {
    setBusy(true);
    try {
      if (step === 0 || step === 1) {
        const ok = await saveIdentity();
        if (!ok) return;
      }
      if (step === 2) {
        // Staff creation is optional — allow skip via "Skip for now"
        if (staff.username || staff.name || staff.password) {
          const ok = await saveStaff();
          if (!ok) return;
          toast.success(`Account for ${staff.name} created`);
        }
      }
      // Step 3 (seed) handles its own upload before advancing
      setStep((s) => s + 1);
    } finally { setBusy(false); }
  }

  async function skipStaff() {
    setStep(3);
  }

  async function skipSeed() {
    setStep(4);
  }

  const currentStep = STEPS[step];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: `linear-gradient(135deg, ${brandPrimary}dd, #09090b)` }}
      data-testid="onboarding-wizard"
    >
      <div className="bg-white w-full max-w-2xl shadow-2xl">
        {/* Progress rail */}
        <div className="flex border-b border-[#e4e4e7]">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div
                key={s.key}
                className={`flex-1 flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  active ? "border-black" : done ? "border-[#10B981]" : "border-transparent"
                }`}
                data-testid={`onboarding-step-${s.key}`}
              >
                <div
                  className={`h-6 w-6 flex items-center justify-center rounded-full text-white text-xs font-bold ${
                    done ? "bg-[#10B981]" : active ? "bg-black" : "bg-[#d4d4d8]"
                  }`}
                >
                  {done ? <CheckCircle size={14} weight="fill" /> : i + 1}
                </div>
                <div className={`text-xs font-semibold ${active ? "text-black" : done ? "text-[#10B981]" : "text-[#a1a1aa]"}`}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-8 space-y-6 min-h-[380px]">
          {step === 0 && (
            <IdentityStep
              name={name} setName={setName}
              contactEmail={contactEmail} setContactEmail={setContactEmail}
              contactPhone={contactPhone} setContactPhone={setContactPhone}
              orgCode={user.org.code}
            />
          )}
          {step === 1 && (
            <BrandingStep
              logoUrl={logoUrl} setLogoUrl={setLogoUrl}
              onUpload={handleLogoFile} uploading={uploading} fileRef={fileRef}
              brandPrimary={brandPrimary} setBrandPrimary={setBrandPrimary}
              brandAccent={brandAccent} setBrandAccent={setBrandAccent}
              orgName={name}
            />
          )}
          {step === 2 && (
            <StaffStep
              staff={staff}
              setStaff={setStaff}
              onSkip={skipStaff}
            />
          )}
          {step === 3 && (
            <SeedDataStep onSkip={skipSeed} onAdvance={() => setStep(4)} />
          )}
          {step === 4 && (
            <DoneStep
              orgName={name}
              brandPrimary={brandPrimary}
              onEnter={async () => { await finishOnboarding(); }}
              busy={busy}
            />
          )}
        </div>

        {step < 4 && step !== 3 && (
          <div className="flex items-center justify-between px-8 py-4 border-t border-[#e4e4e7] bg-[#fafafa]">
            <button
              className="text-sm text-[#71717a] hover:text-black flex items-center gap-1 disabled:opacity-40"
              disabled={step === 0 || busy}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              data-testid="onboarding-back"
            >
              <ArrowLeft size={14} weight="bold" /> Back
            </button>
            <div className="text-xs text-[#71717a]">
              Step {step + 1} of {STEPS.length}
            </div>
            <button
              data-testid="onboarding-next"
              onClick={nextStep}
              disabled={busy || uploading}
              className="text-white text-sm font-medium px-5 py-2.5 flex items-center gap-2 disabled:opacity-60"
              style={{ background: brandPrimary }}
            >
              {busy ? "Saving…" : step === 2 ? "Create & continue" : "Continue"} <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function IdentityStep({ name, setName, contactEmail, setContactEmail, contactPhone, setContactPhone, orgCode }) {
  return (
    <>
      <div>
        <div className="phizo-label text-[10px] text-[#a1a1aa]">Welcome</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter">Let's set up your control room.</h2>
        <p className="text-sm text-[#71717a] mt-2">
          Your LUROMA-hosted workspace is ready. This 4-step setup takes about a minute.
          You can revisit any of these later in Settings.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 pt-4">
        <Field label="Organisation name" required>
          <input
            data-testid="onboarding-org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-[#e4e4e7] bg-white px-3 py-2.5 text-base focus:border-black focus:ring-2 focus:ring-black/10 outline-none"
            placeholder="e.g. Acme Mining Ltd"
            autoFocus
          />
          <div className="text-[10px] text-[#a1a1aa] mt-1">Code: <code className="mono">{orgCode}</code> · fixed by LUROMA</div>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Contact email">
            <input
              data-testid="onboarding-contact-email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
              placeholder="ops@acme.co.zm"
            />
          </Field>
          <Field label="Contact phone">
            <input
              data-testid="onboarding-contact-phone"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
              placeholder="+260 …"
            />
          </Field>
        </div>
      </div>
    </>
  );
}

function BrandingStep({ logoUrl, setLogoUrl, onUpload, uploading, fileRef, brandPrimary, setBrandPrimary, brandAccent, setBrandAccent, orgName }) {
  return (
    <>
      <div>
        <div className="phizo-label text-[10px] text-[#a1a1aa]">Branding</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter">Make it feel like home.</h2>
        <p className="text-sm text-[#71717a] mt-2">
          Your logo drives the sidebar and print reports. Colours theme the header and buttons.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
        <div>
          <div className="phizo-label text-[10px] mb-2">Logo</div>
          <div className="border-2 border-dashed border-[#e4e4e7] p-6 flex flex-col items-center justify-center gap-3 min-h-[180px]">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="max-h-24 max-w-full object-contain" data-testid="onboarding-logo-preview" />
            ) : (
              <ImageIcon size={40} weight="thin" className="text-[#a1a1aa]" />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid="onboarding-logo-file"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="onboarding-logo-upload-btn"
              className="text-xs border border-[#e4e4e7] px-3 py-1.5 hover:bg-white flex items-center gap-1.5 disabled:opacity-60"
            >
              <UploadSimple size={12} weight="bold" /> {uploading ? "Uploading…" : logoUrl ? "Replace" : "Upload logo"}
            </button>
            {logoUrl && (
              <button
                type="button"
                className="text-[10px] text-[#a1a1aa] hover:text-black"
                onClick={() => setLogoUrl("")}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="phizo-label text-[10px] mb-2">Brand colours</div>
          <ColorPicker label="Primary" value={brandPrimary} onChange={setBrandPrimary} testId="onboarding-brand-primary" />
          <div className="h-3" />
          <ColorPicker label="Accent" value={brandAccent} onChange={setBrandAccent} testId="onboarding-brand-accent" />

          <div
            className="mt-4 p-3 text-white text-sm font-semibold flex items-center gap-2"
            style={{ background: brandPrimary }}
          >
            <div className="h-5 w-5 rounded-sm" style={{ background: brandAccent }} />
            Preview · {orgName || "Your organisation"}
          </div>
        </div>
      </div>
    </>
  );
}

function ColorPicker({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[10px] text-[#71717a] mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-9 border border-[#e4e4e7]"
          data-testid={testId}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 border border-[#e4e4e7] px-2 py-1 text-xs mono uppercase"
        />
        <div className="flex gap-1 flex-1 flex-wrap">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`w-5 h-5 border ${value.toLowerCase() === c.toLowerCase() ? "border-black ring-2 ring-black/20" : "border-[#e4e4e7]"}`}
              style={{ background: c }}
              onClick={() => onChange(c)}
              title={c}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StaffStep({ staff, setStaff, onSkip }) {
  return (
    <>
      <div>
        <div className="phizo-label text-[10px] text-[#a1a1aa]">Team</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter">Add your first teammate.</h2>
        <p className="text-sm text-[#71717a] mt-2">
          Create an account for someone on your team. You can skip this and add people later from the Users page.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <Field label="Full name">
          <input
            data-testid="onboarding-staff-name"
            value={staff.name}
            onChange={(e) => setStaff({ ...staff, name: e.target.value })}
            className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
            placeholder="Jane Musonda"
          />
        </Field>
        <Field label="Username">
          <input
            data-testid="onboarding-staff-username"
            value={staff.username}
            onChange={(e) => setStaff({ ...staff, username: e.target.value })}
            className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
            placeholder="jane"
          />
        </Field>
        <Field label="Email (optional)">
          <input
            data-testid="onboarding-staff-email"
            value={staff.email}
            onChange={(e) => setStaff({ ...staff, email: e.target.value })}
            className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
            placeholder="jane@acme.co.zm"
          />
        </Field>
        <Field label="Role">
          <select
            data-testid="onboarding-staff-role"
            value={staff.role}
            onChange={(e) => setStaff({ ...staff, role: e.target.value })}
            className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
          >
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="finance">Finance</option>
            <option value="hr">HR</option>
            <option value="viewer">Viewer</option>
          </select>
        </Field>
        <Field label="Temporary password" span={2}>
          <input
            data-testid="onboarding-staff-password"
            value={staff.password}
            onChange={(e) => setStaff({ ...staff, password: e.target.value })}
            className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
            placeholder="Min 6 chars — they'll change it on first login"
          />
        </Field>
      </div>
      <button
        type="button"
        className="text-xs text-[#71717a] hover:text-black underline"
        onClick={onSkip}
        data-testid="onboarding-skip-staff"
      >
        Skip for now
      </button>
    </>
  );
}

// Target schema for each import kind. `known` are the field names the backend
// expects; `synonyms` maps common header variants → the canonical field.
const IMPORT_SCHEMA = {
  inventory: {
    known: ["sku", "name", "category", "unit", "unit_cost", "quantity", "reorder_level", "location", "supplier", "notes"],
    required: ["sku", "name"],
    synonyms: {
      code: "sku", "item_code": "sku", "product_code": "sku",
      "item_name": "name", "product_name": "name", "description": "name",
      "type": "category", "group": "category",
      "uom": "unit", "measure": "unit",
      "cost": "unit_cost", "price": "unit_cost", "unit_price": "unit_cost",
      "qty": "quantity", "on_hand": "quantity", "stock": "quantity", "quantity_on_hand": "quantity",
      "reorder": "reorder_level", "min": "reorder_level", "min_stock": "reorder_level",
      "bin": "location", "warehouse": "location", "store": "location",
      "vendor": "supplier",
    },
  },
  employees: {
    known: ["employee_code", "full_name", "position", "department", "nrc_number", "phone", "email", "date_hired"],
    required: ["employee_code", "full_name"],
    synonyms: {
      code: "employee_code", "emp_code": "employee_code", "id": "employee_code", "employee_id": "employee_code",
      "name": "full_name", "full_names": "full_name", "employee_name": "full_name",
      "title": "position", "role": "position", "job_title": "position",
      "dept": "department", "team": "department",
      "nrc": "nrc_number", "national_id": "nrc_number", "id_number": "nrc_number",
      "mobile": "phone", "phone_number": "phone", "cell": "phone",
      "hired": "date_hired", "start_date": "date_hired", "date_of_hire": "date_hired",
    },
  },
};

function SeedDataStep({ onSkip, onAdvance }) {
  const [kind, setKind] = useState("inventory"); // "inventory" | "employees"
  const [rows, setRows] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // {csvHeader -> targetField | "" }
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  function parseCSV(text) {
    // Simple CSV parser: split lines, split cols on comma. Strips quotes.
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
    if (!lines.length) return { headers: [], rows: [] };
    const splitLine = (l) => {
      const out = [];
      let cur = "";
      let inQ = false;
      for (const ch of l) {
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === "," && !inQ) { out.push(cur.trim()); cur = ""; continue; }
        cur += ch;
      }
      out.push(cur.trim());
      return out;
    };
    const rawHeaders = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
    const rows = lines.slice(1).map((l) => {
      const cols = splitLine(l);
      const o = {};
      rawHeaders.forEach((h, i) => { o[h] = cols[i] ?? ""; });
      return o;
    });
    return { headers: rawHeaders, rows };
  }

  function autoMap(hdrs) {
    const schema = IMPORT_SCHEMA[kind];
    const knownSet = new Set(schema.known);
    const map = {};
    hdrs.forEach((h) => {
      if (knownSet.has(h)) { map[h] = h; return; }
      if (schema.synonyms[h]) { map[h] = schema.synonyms[h]; return; }
      map[h] = ""; // unmapped — user must decide
    });
    return map;
  }

  async function onPick(f) {
    if (!f) return;
    try {
      const text = await f.text();
      const { headers: h, rows: parsed } = parseCSV(text);
      if (!parsed.length) { toast.error("CSV is empty"); return; }
      setHeaders(h);
      setRows(parsed);
      setMapping(autoMap(h));
      toast.success(`Parsed ${parsed.length} rows from ${f.name}`);
    } catch (e) {
      toast.error("Could not read CSV — is it plain text?");
    }
  }

  // When the kind toggles and we still have a file loaded, re-map to the new schema
  function switchKind(k) {
    setKind(k);
    if (headers.length) setMapping(autoMap(headers));
  }

  function remapRows() {
    // Apply the current mapping: rename keys from csvHeader → targetField;
    // drop columns mapped to "" (ignore).
    return rows.map((r) => {
      const out = {};
      Object.keys(mapping).forEach((csvH) => {
        const target = mapping[csvH];
        if (!target) return;
        out[target] = r[csvH];
      });
      return out;
    });
  }

  const schema = IMPORT_SCHEMA[kind];
  const usedTargets = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = schema.required.filter((r) => !usedTargets.has(r));
  const unmappedHeaders = headers.filter((h) => !mapping[h]);
  const hasIssues = missingRequired.length > 0;

  async function importNow() {
    if (!rows || !rows.length) return;
    if (hasIssues) {
      toast.error(`Map columns for: ${missingRequired.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const remapped = remapRows();
      const url = kind === "inventory" ? "/inventory/bulk-import" : "/employees/bulk-import";
      const { data } = await api.post(url, { rows: remapped });
      const created = data.inserted ?? 0;
      const updated = data.updated ?? 0;
      toast.success(`Imported ${created} new · ${updated} updated`);
      onAdvance();
    } catch (e) {
      toast.error(formatError(e));
    } finally { setBusy(false); }
  }

  const templates = {
    inventory: "sku,name,category,unit,unit_cost,quantity,reorder_level,location,supplier",
    employees: "employee_code,full_name,position,department,nrc_number",
  };

  function downloadTemplate() {
    const blob = new Blob([templates[kind] + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div>
        <div className="phizo-label text-[10px] text-[#a1a1aa]">Import (optional)</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter">Seed your control room.</h2>
        <p className="text-sm text-[#71717a] mt-2">
          Drop a CSV of your inventory or employees so the dashboard feels lived-in from day one.
          Skip if you'd rather add records manually later.
        </p>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          data-testid="seed-kind-inventory"
          onClick={() => { switchKind("inventory"); setRows(null); }}
          className={`flex-1 border px-4 py-3 text-left ${kind === "inventory" ? "border-black bg-[#fafafa]" : "border-[#e4e4e7]"}`}
        >
          <div className="phizo-label text-[10px]">Inventory</div>
          <div className="text-sm font-semibold">SKUs & stock levels</div>
        </button>
        <button
          type="button"
          data-testid="seed-kind-employees"
          onClick={() => { switchKind("employees"); setRows(null); }}
          className={`flex-1 border px-4 py-3 text-left ${kind === "employees" ? "border-black bg-[#fafafa]" : "border-[#e4e4e7]"}`}
        >
          <div className="phizo-label text-[10px]">Employees</div>
          <div className="text-sm font-semibold">Head-count roster</div>
        </button>
      </div>

      <div className="border-2 border-dashed border-[#e4e4e7] p-6 flex flex-col items-center justify-center gap-3 min-h-[140px]">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid="seed-csv-file"
        />
        {rows && rows.length ? (
          <div className="w-full">
            <div className="flex items-center gap-2 mb-3">
              <FileArrowUp size={20} weight="bold" className="text-[#10B981]" />
              <div className="text-sm font-semibold text-[#166534]">
                {rows.length} rows ready to import
              </div>
              <button
                type="button"
                className="ml-auto text-xs underline text-[#71717a]"
                onClick={() => { setRows(null); setHeaders([]); setMapping({}); if (fileRef.current) fileRef.current.value = ""; }}
                data-testid="seed-replace-file"
              >
                Replace file
              </button>
            </div>
            <ColumnMapper
              headers={headers}
              mapping={mapping}
              onChange={(csvH, target) => setMapping((m) => ({ ...m, [csvH]: target }))}
              schema={schema}
              usedTargets={usedTargets}
              missingRequired={missingRequired}
              unmappedHeaders={unmappedHeaders}
            />
            <PreviewTable rows={remapRows()} />
            <div className="text-[11px] text-[#71717a] mt-2 text-center">
              Showing first {Math.min(5, rows.length)} rows · {rows.length > 5 ? `${rows.length - 5} more will import` : "all rows will import"}
            </div>
          </div>
        ) : (
          <>
            <FileArrowUp size={32} weight="thin" className="text-[#a1a1aa]" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="text-xs border border-[#e4e4e7] px-3 py-1.5 hover:bg-white flex items-center gap-1.5"
              data-testid="seed-pick-file-btn"
            >
              <UploadSimple size={12} weight="bold" /> Choose {kind} CSV
            </button>
            <button
              type="button"
              onClick={downloadTemplate}
              className="text-[11px] text-[#71717a] underline"
              data-testid="seed-download-template"
            >
              Download {kind} template
            </button>
          </>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          className="text-xs text-[#71717a] hover:text-black underline"
          onClick={onSkip}
          data-testid="onboarding-skip-seed"
        >
          Skip — I'll add data later
        </button>
        <button
          type="button"
          data-testid="seed-import-btn"
          onClick={importNow}
          disabled={!rows || busy}
          className="bg-black text-white text-sm font-medium px-5 py-2.5 flex items-center gap-2 disabled:opacity-40"
        >
          {busy ? "Importing…" : "Import & continue"} <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </>
  );
}

function ColumnMapper({ headers, mapping, onChange, schema, usedTargets, missingRequired, unmappedHeaders }) {
  const opts = ["", ...schema.known];
  return (
    <div className="border border-[#e4e4e7] bg-white mb-3" data-testid="seed-column-mapper">
      <div className="px-3 py-2 border-b border-[#e4e4e7] flex items-center gap-2 bg-[#fafafa]">
        <div className="phizo-label text-[10px] font-bold">Column mapping</div>
        {missingRequired.length > 0 ? (
          <div className="text-[10px] font-semibold text-[#c2410c]">
            ⚠ Map required: {missingRequired.join(", ")}
          </div>
        ) : (
          <div className="text-[10px] font-semibold text-[#10B981]">
            ✓ All required columns mapped
          </div>
        )}
        <div className="ml-auto text-[10px] text-[#71717a]">
          {headers.length - unmappedHeaders.length}/{headers.length} mapped
        </div>
      </div>
      <div className="max-h-[160px] overflow-y-auto">
        <table className="w-full text-xs">
          <tbody>
            {headers.map((csvH) => {
              const target = mapping[csvH] || "";
              const isRequired = target && schema.required.includes(target);
              const isDuplicate = target && headers.some((other) => other !== csvH && mapping[other] === target);
              return (
                <tr key={csvH} className="border-b border-[#e4e4e7] last:border-b-0">
                  <td className="px-3 py-1.5 mono text-[#09090b] w-2/5 truncate">
                    {csvH}
                  </td>
                  <td className="px-2 py-1 text-[#a1a1aa] text-center">→</td>
                  <td className="px-3 py-1.5">
                    <select
                      data-testid={`seed-map-${csvH}`}
                      value={target}
                      onChange={(e) => onChange(csvH, e.target.value)}
                      className={`w-full text-xs border px-2 py-1 outline-none ${
                        target === ""
                          ? "border-[#fca5a5] bg-[#fef2f2]"
                          : isDuplicate
                            ? "border-[#f59e0b] bg-[#fffbeb]"
                            : isRequired
                              ? "border-[#10B981] bg-[#f0fdf4]"
                              : "border-[#e4e4e7] bg-white"
                      }`}
                    >
                      {opts.map((o) => (
                        <option key={o || "__ignore"} value={o}>
                          {o === "" ? "— Ignore this column —" : o.replace(/_/g, " ")}
                          {schema.required.includes(o) ? " *" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-1.5 border-t border-[#e4e4e7] text-[10px] text-[#71717a] flex items-center gap-3 bg-[#fafafa]">
        <span>* required field</span>
        <span>·</span>
        <span>green = mapped required · red = unmapped · amber = duplicate target</span>
      </div>
    </div>
  );
}

function PreviewTable({ rows }) {
  const preview = rows.slice(0, 5);
  const headers = Object.keys(preview[0] || {});
  const maxCols = 6;
  const shown = headers.slice(0, maxCols);
  const hiddenCount = Math.max(0, headers.length - maxCols);
  return (
    <div className="w-full overflow-x-auto border border-[#e4e4e7]" data-testid="seed-preview-table">
      <table className="w-full text-xs">
        <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
          <tr>
            {shown.map((h) => (
              <th key={h} className="px-3 py-1.5 text-left phizo-label text-[9px] font-semibold text-[#71717a]">
                {h.replace(/_/g, " ")}
              </th>
            ))}
            {hiddenCount > 0 && (
              <th className="px-3 py-1.5 text-left phizo-label text-[9px] font-semibold text-[#a1a1aa]">
                +{hiddenCount} more
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {preview.map((r, i) => (
            <tr key={i} className="border-b border-[#e4e4e7] last:border-b-0">
              {shown.map((h) => (
                <td key={h} className="px-3 py-1.5 text-[#09090b] truncate max-w-[160px]">
                  {r[h] || <span className="text-[#d4d4d8]">—</span>}
                </td>
              ))}
              {hiddenCount > 0 && (
                <td className="px-3 py-1.5 text-[#a1a1aa] italic">…</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DoneStep({ orgName, brandPrimary, onEnter, busy }) {
  return (
    <div className="text-center py-6 space-y-4">
      <div
        className="mx-auto h-16 w-16 rounded-full flex items-center justify-center text-white"
        style={{ background: brandPrimary }}
      >
        <CheckCircle size={40} weight="fill" />
      </div>
      <h2 className="font-heading text-3xl font-black tracking-tighter">You're all set, {orgName}.</h2>
      <p className="text-sm text-[#71717a] max-w-md mx-auto">
        Your control room is ready. Head to the dashboard to log production, add inventory, and start running operations.
      </p>
      <button
        data-testid="onboarding-enter-dashboard"
        onClick={onEnter}
        disabled={busy}
        className="mt-4 text-white text-sm font-medium px-6 py-3 flex items-center gap-2 mx-auto disabled:opacity-60"
        style={{ background: brandPrimary }}
      >
        {busy ? "Loading…" : <>Enter dashboard <ArrowRight size={14} weight="bold" /></>}
      </button>
    </div>
  );
}

function Field({ label, required, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "sm:col-span-2" : ""}`}>
      <div className="phizo-label text-[10px] mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}
